/**
 * CoachDIA Discovery — Dashboard persistant "Mon profil".
 *
 * Vue stable pour l'utilisateur, quel que soit son état :
 *  - not_started      → CTA "Commencer mon profil" (→ /discovery)
 *  - in_progress      → CTA "Reprendre le test" + progression
 *  - completed_unpaid → "Profil complété — En attente de déverrouillage" + CTA paiement
 *  - paid             → "Profil activé" + accès au rapport
 *
 * Le paiement peut être initié ici si l'utilisateur est revenu après avoir quitté
 * sans payer (cas 2 du spec). Pour refaire un test si déjà payé → CTA "Nouveau profil"
 * qui déclenche un nouveau paiement obligatoire (/discovery/restart).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, CheckCircle2, ArrowRight, Sparkles, Download, RotateCw, Share2, Users, Home, ArrowLeft } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_LABELS = {
  not_started: 'Profil non démarré',
  in_progress: 'Test en cours',
  structured_done: 'Test en cours',
  awaiting_ai: 'Test en cours',
  completed_unpaid: 'Profil complété — En attente de déverrouillage',
  paid: 'Profil activé',
  paid_archived: 'Profil archivé',
};

export default function MyDiscovery() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [paying, setPaying] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const hydrate = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/discovery/status`, { headers: authHeaders() }),
        axios.get(`${API}/discovery/pricing`),
      ]);
      setStatusData(s.data);
      setPricing(p.data);
      // Track view_paywall si l'utilisateur voit la CTA de paiement (completed_unpaid)
      if (s.data?.status === 'completed_unpaid') {
        try { await axios.post(`${API}/discovery/track`, { event: 'view_paywall' }, { headers: authHeaders() }); } catch (_) {}
      }
    } catch (e) {
      toast.error('Impossible de charger votre profil');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login?next=/my-discovery'); return; }
    hydrate();
  }, [user, navigate, hydrate]);

  const payExisting = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/discovery/checkout`, { origin_url: origin }, { headers: authHeaders() });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur paiement');
      setPaying(false);
    }
  };

  const startNewPaidTest = async () => {
    if (restarting) return; // protection anti double-click
    setRestarting(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/discovery/restart`, { origin_url: origin }, { headers: authHeaders() });

      // 💳 Cas 1 — Stripe Checkout (profil payant)
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return; // restarting reste true volontairement (page change)
      }

      // 🎁 Cas 2 — Free flow (promo gratuite active)
      if (r.data?.free_access && r.data?.redirect_url) {
        toast.success('Nouveau profil offert ✓ Redirection en cours…');
        window.location.href = r.data.redirect_url;
        return; // restarting reste true volontairement (page change)
      }

      // ⚠️ Réponse inattendue : on dégèle l'UI plutôt que rester bloqué
      toast.error('Réponse inattendue du serveur — réessaye dans un instant.');
      setRestarting(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du redémarrage');
      setRestarting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F1]">
      <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
    </div>
  );

  const status = statusData?.status || 'not_started';
  const session = statusData?.session;
  const progress = session?.progress;
  const price = pricing?.amount_eur;
  const basePrice = pricing?.base_price_eur;
  const promoActive = pricing?.promo_active;

  return (
    <div className="min-h-screen bg-[#FBF8F1] pb-16" data-testid="my-discovery-page">
      {/* ═══ Barre de navigation premium (sortie claire) ═══ */}
      <div className="sticky top-0 z-40 bg-[#FBF8F1]/95 backdrop-blur-sm border-b border-[#E5DFD0]">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/training')}
            data-testid="my-discovery-back-btn"
            className="flex items-center gap-2 text-[13px] text-[#4B5563] hover:text-[#0F0F10] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <button
            onClick={() => navigate('/training')}
            data-testid="my-discovery-home-btn"
            className="flex items-center gap-2 text-[13px] text-[#92710A] hover:text-[#0F0F10] transition"
          >
            <Home className="w-4 h-4" />
            Tableau de bord
          </button>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 pt-12">
        <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase">COACHDIA · MON PROFIL PRISM</div>
        <h1 className="font-serif text-4xl text-[#0F0F10] mt-2">Mon profil</h1>
        <p className="text-[#4B5563] mt-2" data-testid="my-discovery-status-label">
          Statut : <b>{STATUS_LABELS[status] || status}</b>
        </p>

        {/* ────────── NOT STARTED ────────── */}
        {status === 'not_started' && (
          <Card className="mt-8 p-8 bg-white border border-[#E5DFD0] rounded-sm" data-testid="my-discovery-not-started">
            <div className="text-center">
              <Sparkles className="w-6 h-6 text-[#92710A] mx-auto mb-3" />
              <h2 className="font-serif text-2xl">Commencez votre profil</h2>
              <p className="text-[#4B5563] mt-2 text-sm max-w-md mx-auto">
                Une expérience de 12 à 15 minutes, entièrement personnalisée. Vous ne paierez qu'à la fin, pour débloquer votre rapport complet si vous le souhaitez.
              </p>
              <Button
                data-testid="my-discovery-start-btn"
                onClick={() => navigate('/discovery')}
                className="mt-6 bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full px-10 py-6"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Commencer l'expérience
              </Button>
            </div>
          </Card>
        )}

        {/* ────────── IN PROGRESS ────────── */}
        {['in_progress', 'structured_done', 'awaiting_ai'].includes(status) && (
          <Card className="mt-8 p-8 bg-white border border-[#E5DFD0] rounded-sm" data-testid="my-discovery-in-progress">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-5 h-5 text-[#92710A]" />
              <h2 className="font-serif text-2xl text-[#0F0F10]">Votre test est en cours</h2>
            </div>
            {progress && (
              <>
                <div className="w-full h-[4px] bg-[#E5DFD0] rounded-full overflow-hidden my-3">
                  <div
                    className="h-full bg-[#92710A] transition-all duration-500"
                    style={{ width: `${progress.percent || 0}%` }}
                  />
                </div>
                <div className="text-xs text-[#4B5563]">
                  {progress.structured_done} / {progress.structured_total} questions structurées · {progress.ai_done} / {progress.ai_total} approfondissements IA
                </div>
              </>
            )}
            <p className="text-sm text-[#4B5563] mt-4">
              Vos réponses sont automatiquement sauvegardées. Vous pouvez reprendre à tout moment.
            </p>
            <Button
              data-testid="my-discovery-resume-btn"
              onClick={() => navigate('/discovery')}
              className="mt-6 bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full px-8"
            >
              <ArrowRight className="w-4 h-4 mr-2" /> Reprendre le test
            </Button>
          </Card>
        )}

        {/* ────────── COMPLETED UNPAID ────────── */}
        {status === 'completed_unpaid' && (
          <Card className="mt-8 bg-[#0F0F10] text-white border-[#92710A] rounded-sm overflow-hidden" data-testid="my-discovery-unpaid">
            <div className="p-8 text-center">
              <Lock className="w-8 h-8 text-[#92710A] mx-auto mb-3" />
              <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase">Profil complété</div>
              <h2 className="font-serif text-3xl mt-2">Votre rapport vous attend</h2>
              <p className="text-[#C4B27A] text-sm italic mt-2">
                Vous avez terminé votre profil — il ne reste qu'à le déverrouiller pour accéder à votre rapport complet, votre plan 30/60/90 et votre sélection de coachs.
              </p>

              <div className="grid sm:grid-cols-3 gap-5 my-8 text-sm">
                <div>
                  <div className="text-[#92710A] font-serif text-2xl">30</div>
                  <div className="text-xs tracking-widest text-[#C4B27A] uppercase">pages premium</div>
                </div>
                <div>
                  <div className="text-[#92710A] font-serif text-2xl">12</div>
                  <div className="text-xs tracking-widest text-[#C4B27A] uppercase">archetypes possibles</div>
                </div>
                <div>
                  <div className="text-[#92710A] font-serif text-2xl">90j</div>
                  <div className="text-xs tracking-widest text-[#C4B27A] uppercase">plan actionnable</div>
                </div>
              </div>

              <Button
                data-testid="my-discovery-unlock-btn"
                onClick={payExisting}
                disabled={paying}
                className="bg-[#92710A] hover:bg-[#A88415] text-white rounded-full px-10 py-6 text-base font-serif tracking-wide"
              >
                {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Débloquer mon rapport
                {price != null && (
                  <span className="ml-3 opacity-90" data-testid="my-discovery-unlock-price">
                    {promoActive && basePrice > price ? (
                      <>· <s className="opacity-60">{basePrice.toFixed(0)} €</s> {price.toFixed(0)} €</>
                    ) : (
                      <>· {price.toFixed(0)} €</>
                    )}
                  </span>
                )}
              </Button>
              {promoActive && pricing?.promo_label && (
                <div className="mt-3 text-[11px] text-[#C4B27A] tracking-wide" data-testid="my-discovery-promo-label">
                  🎁 {pricing.promo_label} · -{pricing.discount_pct?.toFixed(0)}%
                </div>
              )}
              <div className="mt-4 text-[11px] text-[#8E8878]">Paiement sécurisé Stripe · Accès immédiat après paiement</div>
            </div>
          </Card>
        )}

        {/* ────────── PAID ────────── */}
        {status === 'paid' && (
          <Card className="mt-8 p-8 bg-white border border-[#E5DFD0] rounded-sm" data-testid="my-discovery-paid">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#2D8659]" />
              <h2 className="font-serif text-2xl">Votre profil est activé</h2>
            </div>
            <p className="text-sm text-[#4B5563] mt-3">
              Accédez à tout moment à votre rapport complet, téléchargez le PDF, recevez-le par email ou découvrez vos coachs recommandés.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                data-testid="my-discovery-view-report"
                onClick={() => navigate('/discovery')}
                className="bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full"
              >
                <ArrowRight className="w-4 h-4 mr-2" /> Voir mon rapport
              </Button>
              <Button
                data-testid="my-discovery-download-pdf"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/discovery/report/pdf`, {
                      headers: authHeaders(), responseType: 'blob',
                    });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url; a.download = 'coachdia-prism.pdf'; a.click();
                    URL.revokeObjectURL(url);
                  } catch (_) { toast.error('Téléchargement impossible'); }
                }}
                variant="outline"
                className="rounded-full border-[#C4B27A] text-[#0F0F10]"
              >
                <Download className="w-4 h-4 mr-2" /> Télécharger le PDF
              </Button>
              <Button
                data-testid="my-discovery-coaches"
                onClick={() => navigate('/discovery/coaches')}
                variant="outline"
                className="rounded-full border-[#C4B27A] text-[#0F0F10]"
              >
                Coachs recommandés
              </Button>
              <Button
                data-testid="my-discovery-share-btn"
                onClick={() => navigate('/discovery')}
                variant="outline"
                className="rounded-full border-[#92710A] text-[#92710A] hover:bg-[#FBF8F1]"
              >
                <Share2 className="w-4 h-4 mr-2" /> Partager mon profil
              </Button>
              <Button
                data-testid="my-discovery-team-insights-btn"
                onClick={() => navigate('/team-pulse/teams')}
                variant="outline"
                className="rounded-full border-[#0F0F10] text-[#0F0F10] hover:bg-stone-100"
              >
                <Users className="w-4 h-4 mr-2" /> Analyser mon équipe
              </Button>
            </div>

            {/* Restart — nouveau test payant */}
            <div className="mt-10 pt-6 border-t border-[#E5DFD0]">
              <h3 className="font-serif text-lg text-[#0F0F10] mb-2">Refaire un profil</h3>
              <p className="text-xs text-[#4B5563] leading-relaxed mb-3">
                Un nouveau profil donne lieu à une nouvelle analyse complète. Chaque nouveau profil nécessite une nouvelle facturation — cela garantit la fiabilité et l'unicité de chaque rapport.
              </p>
              <Button
                data-testid="my-discovery-restart-btn"
                onClick={startNewPaidTest}
                disabled={restarting}
                variant="outline"
                className="rounded-full border-[#C4B27A] text-[#0F0F10]"
              >
                {restarting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCw className="w-4 h-4 mr-2" />}
                Démarrer un nouveau profil
                {price != null && <span className="ml-2 opacity-70 text-xs">· {price.toFixed(0)} €</span>}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
